import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect, useState } from "react";
import {
  currentNetworkOnlineStatus,
  initNetworkStatus,
  subscribeNetworkStatus,
} from "~/config/capacitor/network/hook";
import { recordSpeechEvent } from "./snag-speech-telemetry";
import {
  downloadSpeechModel,
  getSpeechModelStatus,
  snagDictationLanguage,
} from "./snag-vocal-transcription";
import { createSpeechModelPreparation } from "./speech-model-preparation";

let appActive = true;
const preparation = createSpeechModelPreparation({
  download: () => downloadSpeechModel(snagDictationLanguage),
  getStatus: () => getSpeechModelStatus(snagDictationLanguage),
  isActive: () => appActive && document.visibilityState !== "hidden",
  isOnline: () => currentNetworkOnlineStatus,
  onEvent: (event, details, warning) =>
    recordSpeechEvent(
      event,
      { ...details, language: snagDictationLanguage },
      warning
    ),
});
let cleanup: (() => void) | undefined;

export function initSnagSpeechModelPreparation() {
  if (!Capacitor.isNativePlatform()) return;
  if (cleanup) return cleanup;
  appActive = true;
  let disposed = false;
  let removeAppListener: (() => Promise<void>) | undefined;
  let receivedAppEvent = false;
  const refresh = () => {
    void preparation.refresh();
  };
  const unsubscribe = subscribeNetworkStatus(refresh);
  document.addEventListener("visibilitychange", refresh);
  cleanup = () => {
    disposed = true;
    preparation.stop();
    unsubscribe();
    document.removeEventListener("visibilitychange", refresh);
    void removeAppListener?.();
    cleanup = undefined;
  };
  preparation.start();
  void initNetworkStatus().then(() => {
    if (!disposed) refresh();
  });
  if (Capacitor.isPluginAvailable("App")) {
    void App.addListener("appStateChange", ({ isActive }) => {
      if (disposed) return;
      receivedAppEvent = true;
      appActive = isActive;
      refresh();
    })
      .then((handle) => {
        if (disposed) void handle.remove();
        else removeAppListener = () => handle.remove();
      })
      .catch(() => {
        /* WebView visibility remains a fallback. */
      });
    void App.getState()
      .then(({ isActive }) => {
        if (disposed || receivedAppEvent) return;
        appActive = isActive;
        refresh();
      })
      .catch(() => {
        /* WebView visibility remains a fallback. */
      });
  }
  return cleanup;
}

export function useSnagSpeechModelStatus() {
  const [status, setStatus] = useState(preparation.getSnapshot);
  useEffect(() => {
    const update = () => setStatus(preparation.getSnapshot());
    const unsubscribe = preparation.subscribe(update);
    update();
    // Unmounting an editor never stops the application-wide preparation.
    return unsubscribe;
  }, []);
  return status;
}

export const pauseSnagSpeechModelPreparation = preparation.pause;
